import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useDeepZoomViewer } from "~/hook/useDeepZoomViewer";
import { useKeyboardShortcuts } from "~/hook/useKeyboardShortcuts";
import { DeepZoomViewerProps } from "~/type";
import ControlBar from "./ControlBar";
import "./DeepZoomViewer.css";

const DeepZoomViewer: React.FC<DeepZoomViewerProps> = ({ dziUrl, imageWidth, imageHeight, config = {}, downloadUrl, downloadUrlHigh, isWeb }) => {
  const [searchParams] = useSearchParams();
  const isDarkMode = useMemo(() => searchParams.get("theme") === "dark", [searchParams]);
  const { viewerRef, state, initializeViewer, zoomIn, zoomOut, zoomTo, fitBounds, reset, requestFullscreen } = useDeepZoomViewer(config);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const viewerContainerRef = React.useRef<HTMLDivElement>(null);

  // Enable keyboard shortcuts
  useKeyboardShortcuts({
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onFit: fitBounds,
    onReset: reset,
  });

  useEffect(() => {
    initializeViewer("openseadragon-viewer", dziUrl);

    return () => {
      if (viewerRef.current) {
        viewerRef.current.destroy();
      }
    };
  }, [dziUrl, initializeViewer]);

  const handleFullscreen = () => {
    if (containerRef.current) {
      requestFullscreen(containerRef.current);
    }
  };

  const handleZoomSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    const zoomLevel = 0.5 + (value / 100) * 9.5;
    zoomTo(zoomLevel);
  };

  return (
    <div className={`viewer-container relative shadow-lg ${isDarkMode ? "dark-mode" : "rounded-lg "}`} ref={containerRef}>
      <div className="openseadragon-viewer-container  margin-0" ref={viewerContainerRef}>
        <div id="openseadragon-viewer"></div>
      </div>

      <ControlBar
        zoomLevel={Math.round(state.zoom * 100)}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onZoomSlider={handleZoomSlider}
        onFit={fitBounds}
        onReset={reset}
        onFullscreen={handleFullscreen}
        zoomSliderValue={((state.zoom - 0.5) / 9.5) * 100}
        downloadUrl={downloadUrl}
        downloadUrlHigh={downloadUrlHigh}
        isWeb={isWeb}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default DeepZoomViewer;
